import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material'
import { settingsApi } from '@api/endpoints/settings'
import { NotificationFrequency } from '@/types/settings'
import type {
  UserNotificationSettingsDto,
  EmailNotificationSettingsDto,
  SmsNotificationSettingsDto,
} from '@/types/settings'
import { ErrorDialog } from '@components/ErrorDialog'
import { getSaveFriendlyError, type FriendlyError } from '@utils/errorHandling'

// Helper type for UI state
type FrequencyString = 'Immediately' | 'Daily' | 'Weekly' | 'Never'

interface EmailSettingsUI {
  summaryOverview: boolean
  newPublicActivities: FrequencyString
  friendRequests: FrequencyString
  publishedItemActivities: FrequencyString
  messages: FrequencyString
}

interface SmsSettingsUI {
  enabled: boolean
  frequency: FrequencyString
  friendRequests: boolean
  messages: boolean
}

// Convert NotificationFrequency enum to string
function frequencyToString(freq: NotificationFrequency | number): FrequencyString {
  switch (freq) {
    case NotificationFrequency.Never:
      return 'Never'
    case NotificationFrequency.Immediately:
      return 'Immediately'
    case NotificationFrequency.Daily:
      return 'Daily'
    case NotificationFrequency.Weekly:
      return 'Weekly'
    default:
      return 'Daily'
  }
}

// Convert string to NotificationFrequency enum
function stringToFrequency(str: FrequencyString): NotificationFrequency {
  switch (str) {
    case 'Immediately':
      return NotificationFrequency.Immediately
    case 'Daily':
      return NotificationFrequency.Daily
    case 'Weekly':
      return NotificationFrequency.Weekly
    case 'Never':
      return NotificationFrequency.Never
    default:
      return NotificationFrequency.Daily
  }
}

export function NotificationSettings() {
  const [settings, setSettings] = useState<UserNotificationSettingsDto | null>(null)
  const [emailSettings, setEmailSettings] = useState<EmailSettingsUI>({
    summaryOverview: true,
    newPublicActivities: 'Immediately',
    friendRequests: 'Immediately',
    publishedItemActivities: 'Daily',
    messages: 'Immediately',
  })
  const [smsSettings, setSmsSettings] = useState<SmsSettingsUI>({
    enabled: false,
    frequency: 'Daily',
    friendRequests: false,
    messages: false,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Load notification preferences
  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setIsLoading(true)
      const data = await settingsApi.getUserNotificationSettings()
      console.log('[API Response] /api/me/settings/notifications', data)
      setSettings(data)

      // Convert API enum values to UI strings
      setEmailSettings({
        summaryOverview: data.email.summaryOverview,
        newPublicActivities: frequencyToString(data.email.newPublicActivities),
        friendRequests: frequencyToString(data.email.friendRequests),
        publishedItemActivities: frequencyToString(data.email.publishedItemActivities),
        messages: frequencyToString(data.email.messages),
      })

      setSmsSettings({
        enabled: data.sms.enabled,
        frequency: frequencyToString(data.sms.frequency),
        friendRequests: data.sms.friendRequests,
        messages: data.sms.messages,
      })
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmailSettingChange = (field: keyof EmailSettingsUI) => (event: any) => {
    const value = field === 'summaryOverview' ? event.target.checked : event.target.value
    setEmailSettings({
      ...emailSettings,
      [field]: value,
    })
    setSuccessMessage(null)
  }

  const handleSmsToggle = (field: keyof SmsSettingsUI) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSmsSettings({
      ...smsSettings,
      [field]: event.target.checked,
    })
    setSuccessMessage(null)
  }

  const handleSmsFrequencyChange = (event: any) => {
    setSmsSettings({
      ...smsSettings,
      frequency: event.target.value,
    })
    setSuccessMessage(null)
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      setSuccessMessage(null)

      // Convert UI strings to API enum values
      const emailSettingsDto: EmailNotificationSettingsDto = {
        summaryOverview: emailSettings.summaryOverview,
        newPublicActivities: stringToFrequency(emailSettings.newPublicActivities),
        friendRequests: stringToFrequency(emailSettings.friendRequests),
        publishedItemActivities: stringToFrequency(emailSettings.publishedItemActivities),
        messages: stringToFrequency(emailSettings.messages),
      }

      const smsSettingsDto: SmsNotificationSettingsDto = {
        enabled: smsSettings.enabled,
        frequency: stringToFrequency(smsSettings.frequency),
        friendRequests: smsSettings.friendRequests,
        messages: smsSettings.messages,
      }

      await settingsApi.updateUserNotificationSettings({
        email: emailSettingsDto,
        sms: smsSettingsDto,
      })

      setSuccessMessage('Notification settings updated successfully!')
      await loadSettings()
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSaving(false)
    }
  }

  const frequencyOptions: FrequencyString[] = ['Immediately', 'Daily', 'Weekly', 'Never']

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Error Dialog */}
      {error && (
        <ErrorDialog
          open={true}
          title={error.title}
          message={error.message}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Email Notifications */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Email Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose how often you'd like to receive email notifications for different activities.
        </Typography>

        <Box display="flex" flexDirection="column" gap={2} mb={3}>
          <FormControlLabel
            control={
              <Switch
                checked={emailSettings.summaryOverview}
                onChange={handleEmailSettingChange('summaryOverview')}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Daily Summary Email</Typography>
                <Typography variant="caption" color="text.secondary">
                  Receive a daily overview of your activities and notifications
                </Typography>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>New Public Activities</InputLabel>
              <Select
                value={emailSettings.newPublicActivities || 'Never'}
                onChange={handleEmailSettingChange('newPublicActivities')}
                label="New Public Activities"
              >
                {frequencyOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Friend Requests</InputLabel>
              <Select
                value={emailSettings.friendRequests || 'Immediately'}
                onChange={handleEmailSettingChange('friendRequests')}
                label="Friend Requests"
              >
                {frequencyOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Published Item Activities</InputLabel>
              <Select
                value={emailSettings.publishedItemActivities || 'Daily'}
                onChange={handleEmailSettingChange('publishedItemActivities')}
                label="Published Item Activities"
              >
                {frequencyOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Messages</InputLabel>
              <Select
                value={emailSettings.messages || 'Immediately'}
                onChange={handleEmailSettingChange('messages')}
                label="Messages"
              >
                {frequencyOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* SMS Notifications */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          SMS Notifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Receive text messages for important notifications. Standard messaging rates may apply.
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={smsSettings.enabled}
                onChange={handleSmsToggle('enabled')}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Enable SMS Notifications</Typography>
                <Typography variant="caption" color="text.secondary">
                  Turn on SMS notifications for important updates
                </Typography>
              </Box>
            }
          />

          {smsSettings.enabled && (
            <>
              <Divider />

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>SMS Frequency</InputLabel>
                <Select
                  value={smsSettings.frequency || 'Daily'}
                  onChange={handleSmsFrequencyChange}
                  label="SMS Frequency"
                >
                  {frequencyOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={smsSettings.friendRequests}
                    onChange={handleSmsToggle('friendRequests')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Friend Requests</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receive SMS when someone sends you a friend request
                    </Typography>
                  </Box>
                }
              />

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={smsSettings.messages}
                    onChange={handleSmsToggle('messages')}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Messages</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receive SMS for new messages
                    </Typography>
                  </Box>
                }
              />
            </>
          )}
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={loadSettings} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  )
}
